/*
=========================================================
* RingFoods Free Business Webpage Section
=========================================================
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";

// @mui icons
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import TakeoutDiningIcon from "@mui/icons-material/TakeoutDining";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import RateReviewIcon from "@mui/icons-material/RateReview";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LanguageIcon from "@mui/icons-material/Language";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { brandPrimary, grey100, grey300 } from "pages/RingFoodsHomepage/theme";

const features = [
  {
    icon: EventAvailableIcon,
    title: "Online Reservations",
    description:
      "Guests book a table straight from your page. Same live availability the AI uses on the phone — no double-booking — with an instant SMS confirmation to the guest.",
  },
  {
    icon: TakeoutDiningIcon,
    title: "Online Ordering",
    description:
      "Takeout and delivery orders placed on your page, paid at pickup, flowing into the same order dashboard as phone orders. No commission-hungry third-party apps.",
  },
  {
    icon: HourglassTopIcon,
    title: "Online Waitlist & Queue",
    description:
      "Fully booked? Guests join your waitlist from the page or over the phone, see their live position in line, and get texted automatically the moment a table opens.",
  },
  {
    icon: RateReviewIcon,
    title: "Customer Feedback",
    description:
      "A feedback form on your page catches compliments and complaints before they become public reviews — classified by sentiment and severity, with instant alerts for critical ones.",
  },
  {
    icon: QrCode2Icon,
    title: "Printable QR Poster",
    description:
      "One click generates a print-ready QR poster for your window, counter, or receipts. Guests scan to book, order, or join the waitlist — without tying up your phone line.",
  },
  {
    icon: MenuBookIcon,
    title: "Menu & Hours, Always Current",
    description:
      "Your menu, photos, hours, and holiday schedules publish straight from your dashboard. Update once — the phone agent and your webpage stay in sync.",
  },
];

function FreeBusinessPage() {
  return (
    <MKBox
      component="section"
      py={{ xs: 8, md: 12 }}
      id="free-website"
      sx={{ backgroundColor: grey100 }}
    >
      <Container>
        {/* Section Header */}
        <Grid container spacing={3} sx={{ mb: { xs: 4, md: 6 } }}>
          <Grid item xs={12} lg={8} mx="auto" textAlign="center">
            <Chip
              label="Included free with every plan"
              sx={{
                backgroundColor: "rgba(34, 197, 94, 0.12)",
                color: "#15803D",
                fontWeight: 700,
                fontSize: "0.8rem",
                mb: 2,
              }}
            />
            <MKTypography
              variant="h2"
              fontWeight="bold"
              color="primary"
              sx={{ mb: 2, fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.5rem" } }}
            >
              Your Restaurant&apos;s Own Website. Free.
            </MKTypography>
            <MKTypography
              variant="body1"
              color="text"
              sx={{ fontSize: { xs: "1rem", md: "1.125rem" } }}
            >
              Every RingFoods plan includes a public webpage for your restaurant &mdash; no web
              developer, no hosting bill, no third-party commissions. Guests book tables, place
              orders, join your waitlist, and leave feedback online while your phone line stays
              free.
            </MKTypography>
          </Grid>
        </Grid>

        {/* URL pill */}
        <Grid container justifyContent="center" sx={{ mb: { xs: 5, md: 7 } }}>
          <Grid item>
            <MKBox
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: { xs: 2, md: 3 },
                py: 1.25,
                borderRadius: 50,
                backgroundColor: "white",
                border: `1px solid ${grey300}`,
                boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              }}
            >
              <LanguageIcon sx={{ fontSize: 20, color: brandPrimary, opacity: 0.6 }} />
              <MKTypography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: brandPrimary,
                  fontSize: { xs: "0.85rem", md: "0.95rem" },
                }}
              >
                www.ringfoods.com/
                <MKBox component="span" sx={{ opacity: 0.5 }}>
                  your-restaurant
                </MKBox>
              </MKTypography>
            </MKBox>
          </Grid>
        </Grid>

        {/* Feature grid */}
        <Grid container spacing={3}>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Grid item xs={12} md={6} key={index}>
                <MKBox
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 2,
                    backgroundColor: "white",
                    border: "1px solid #E5E5E5",
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <MKBox
                    sx={{
                      width: 56,
                      height: 56,
                      minWidth: 56,
                      borderRadius: "50%",
                      backgroundColor: "rgba(26, 26, 26, 0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2.5,
                    }}
                  >
                    <Icon sx={{ fontSize: 28, color: brandPrimary }} />
                  </MKBox>
                  <MKBox>
                    <MKTypography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {feature.title}
                    </MKTypography>
                    <MKTypography
                      variant="body2"
                      color="text"
                      sx={{ opacity: 0.8, lineHeight: 1.6 }}
                    >
                      {feature.description}
                    </MKTypography>
                  </MKBox>
                </MKBox>
              </Grid>
            );
          })}
        </Grid>

        {/* Footnote */}
        <Grid container sx={{ mt: { xs: 4, md: 5 } }}>
          <Grid item xs={12} textAlign="center">
            <MKTypography variant="body2" color="text" sx={{ opacity: 0.7 }}>
              Your page goes live automatically with your phone number. Turn reservations, ordering,
              waitlist, and feedback on or off anytime from your dashboard.
            </MKTypography>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

export default FreeBusinessPage;
